import * as React from 'react'
import { HashFields, Label, TextField } from 'react-rails-form-helpers'
import { AddressForm, AddressFormFields } from '../Address/AddressForm'
import * as API from '../lib/api'

// Collects billing identity (name, optional Church/Organization) and prepends it
// to the shared `AddressForm`. Tax recalc on Country/State/Zip change is owned by
// the shell (`AbstractMembershipCheckoutForm.billingAddressChanged`); this step
// forwards every address change up to it.

// Phone is dropped in the redesign (CD-146 field reduction); email/password
// belong to Step 1.
export type BillingCustomerField = 'first_name' | 'last_name' | 'company'

// Match how react-rails-form-helpers serializes the nested live inputs (e.g.
// `cashier[customer][first_name]`). The collapsed hidden mirror (CD-167) rebuilds
// these same names from the helpers below, so the two cannot drift.
export const BILLING_CUSTOMER_NAMESPACE = 'customer'
export const BILLING_ADDRESS_NAMESPACE = 'billing_address'

export const billingCustomerFieldName = (formName: string, field: BillingCustomerField): string =>
  `${formName}[${BILLING_CUSTOMER_NAMESPACE}][${field}]`

export const billingAddressFieldName = (formName: string, field: AddressFormFields): string =>
  `${formName}[${BILLING_ADDRESS_NAMESPACE}][${field}]`

interface IProps {
  // Owned by the shell so it survives this step unmounting (CD-167). The fields
  // are uncontrolled and push edits up via the callbacks; on reopen the step
  // remounts and rehydrates from these.
  customer: API.ICustomer
  address: API.IAddress
  stateSelectOptions: string[][]
  countrySelectOptions: string[][]
  onCustomerChanged: (field: BillingCustomerField, value: string) => void
  // Wired to `billingAddressChanged`, which records the value and (for
  // Country/State/Zip) recalculates sales tax.
  onAddressChanged: (field: AddressFormFields, value: string) => void
  onContinue: () => void
  // Absent for the control arm, which must keep rendering today's exact tree.
  mapbox?: API.IMapbox
  onAddressFieldsChanged?: (fields: Partial<API.IAddress>) => void
}

export class Step2Billing extends React.Component<IProps, object> {
  // Used to gate Continue on the live fields' constraint validity.
  private rootRef = React.createRef<HTMLDivElement>()

  public handleCustomerBlur = (
    field: BillingCustomerField,
    event: React.FocusEvent<HTMLInputElement>,
  ) => {
    this.props.onCustomerChanged(field, event.currentTarget.value)
  }

  // Continue is `type="button"` and the form isn't submitted until the payment
  // step (by which point this body has unmounted), so the browser never enforces
  // the `required` attributes. Check constraint validity here and surface the
  // native validation bubble on the first invalid field, as Step1Account does.
  public handleContinue = () => {
    const root = this.rootRef.current
    if (root) {
      const fields = Array.from(
        root.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input, select'),
      )
      const invalid = fields.find((field) => !field.checkValidity())
      if (invalid) {
        invalid.reportValidity()
        return
      }
    }

    this.props.onContinue()
  }

  public render() {
    const {
      customer,
      address,
      stateSelectOptions,
      countrySelectOptions,
      onAddressChanged,
    } = this.props
    const { first_name: firstName, last_name: lastName, company } = customer

    return (
      <div className="step2-billing" data-testid="step2-billing" ref={this.rootRef}>
        <HashFields name={BILLING_CUSTOMER_NAMESPACE}>
          {/* small-30 keeps First/Last side-by-side on mobile rather than stacking. */}
          <div className="grid-x">
            <div className="cell small-30 medium-30 small-padded">
              <Label htmlFor="first_name">First Name</Label>
              <TextField
                name="first_name"
                autoComplete="given-name"
                required={true}
                defaultValue={firstName}
                data-1p-ignore="true"
                onBlur={(e) => this.handleCustomerBlur('first_name', e)}
              />
            </div>

            <div className="cell small-30 medium-30 small-padded">
              <Label htmlFor="last_name">Last Name</Label>
              <TextField
                name="last_name"
                autoComplete="family-name"
                required={true}
                defaultValue={lastName}
                data-1p-ignore="true"
                onBlur={(e) => this.handleCustomerBlur('last_name', e)}
              />
            </div>
          </div>

          <Label htmlFor="company">Church/Organization</Label>
          <TextField
            name="company"
            autoComplete="organization"
            defaultValue={company}
            data-1p-ignore="true"
            onBlur={(e) => this.handleCustomerBlur('company', e)}
          />
        </HashFields>

        {/* The shared AddressForm owns the US/non-US State-vs-Province + Zip
            switch; its field order already matches Figma. */}
        <AddressForm
          address={address}
          addressType="billing"
          stateSelectOptions={stateSelectOptions}
          countrySelectOptions={countrySelectOptions}
          toggleUseDefaultBillingAddress={() => undefined}
          allowUsingDefault={false}
          onAddressChanged={onAddressChanged}
          mapbox={this.props.mapbox}
          onAddressFieldsChanged={this.props.onAddressFieldsChanged}
        />

        <button
          type="button"
          className="button trial-redesign__continue step2-billing__continue"
          data-testid="step2-continue"
          onClick={this.handleContinue}
        >
          Continue to Payment Information
        </button>
      </div>
    )
  }
}
